import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Save } from 'lucide-react';
import {
  supplyAgentService,
  type SupplyAgentLogisticsContainer,
  type SupplyAgentLogisticsProduct,
  type SupplyAgentLogisticsRule,
} from '@/services/supplyAgentService';
import { logisticsCatalogService, type LogisticsUnitAssignment, type LogisticsUnitTemplate } from '@/modules/logisticsCatalog/logisticsCatalogService';
import {
  logisticsConstraintLabel,
  logisticsStrategyLabel,
} from '@/modules/supply/components/logisticsBusinessLabels';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';

type EditorTab = 'products' | 'rules' | 'containers';

const CONSTRAINT_TYPES = [
  'PALLET_MULTIPLE',
  'BASE_MULTIPLE',
  'BOX_MULTIPLE',
  'MIN_CONTAINER_FILL',
] as const;

const DIRECTIONS = [
  'INCREASE_ONLY',
  'DECREASE_ONLY',
  'NEAREST',
  'INCREASE',
  'DECREASE',
] as const;

const STRATEGIES = [
  'ROTATION_WEIGHTED_FILL',
  'MINIMUM_COMPLIANCE',
  'COVERAGE_LIMITED_FILL',
  'MAX_FILL_WITHOUT_EXCEEDING',
  'PROPORTIONAL',
] as const;

const MIX_POLICIES = ['SEPARATE_GROUPS', 'REJECT', 'ALLOW'] as const;

function formatMasterNum(value: number | null | undefined, digits = 2): string {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return Number(value).toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}

interface SupplyAgentLogisticsMasterEditorProps {
  supplierId: number;
  onChanged?: () => void;
}

type ProductDraft = {
  pallet_family: string;
  max_extra_pct: string;
  max_extra_coverage_days: string;
};

type RuleFormState = {
  id?: number;
  name: string;
  external_rule_id: string;
  active: boolean;
  constraint_type: string;
  container_code: string;
  container_template_id: string;
  target_value: string;
  minimum_value: string;
  maximum_value: string;
  constraint_unit: string;
  adjustment_direction: string;
  optimization_strategy: string;
  max_extra_pct_default: string;
  max_extra_coverage_days_default: string;
  incompatible_product_policy: string;
  allow_mixed_products: boolean;
  priority: string;
  notes: string;
};

function emptyRuleForm(): RuleFormState {
  return {
    name: '',
    external_rule_id: '',
    active: true,
    constraint_type: 'PALLET_MULTIPLE',
    container_code: '',
    container_template_id: '',
    target_value: '1',
    minimum_value: '1',
    maximum_value: '',
    constraint_unit: 'PALLET',
    adjustment_direction: 'INCREASE_ONLY',
    optimization_strategy: 'PROPORTIONAL',
    max_extra_pct_default: '100',
    max_extra_coverage_days_default: '60',
    incompatible_product_policy: 'SEPARATE_GROUPS',
    allow_mixed_products: true,
    priority: '100',
    notes: '',
  };
}

function ruleToForm(rule: SupplyAgentLogisticsRule): RuleFormState {
  return {
    id: rule.id,
    name: rule.name || '',
    external_rule_id: rule.external_rule_id || '',
    active: rule.active !== false,
    constraint_type: rule.constraint_type || 'PALLET_MULTIPLE',
    container_code: rule.container_code || '',
    container_template_id:
      rule.container_template_id != null ? String(rule.container_template_id) : '',
    target_value: rule.target_value != null ? String(rule.target_value) : '',
    minimum_value: rule.minimum_value != null ? String(rule.minimum_value) : '',
    maximum_value: rule.maximum_value != null ? String(rule.maximum_value) : '',
    constraint_unit: rule.constraint_unit || '',
    adjustment_direction: rule.adjustment_direction || 'INCREASE_ONLY',
    optimization_strategy: rule.optimization_strategy || 'PROPORTIONAL',
    max_extra_pct_default:
      rule.max_extra_pct_default != null ? String(rule.max_extra_pct_default) : '',
    max_extra_coverage_days_default:
      rule.max_extra_coverage_days_default != null
        ? String(rule.max_extra_coverage_days_default)
        : '',
    incompatible_product_policy: rule.incompatible_product_policy || 'SEPARATE_GROUPS',
    allow_mixed_products: rule.allow_mixed_products !== false,
    priority: rule.priority != null ? String(rule.priority) : '100',
    notes: rule.notes || '',
  };
}

function parseOptionalFloat(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function productToDraft(p: SupplyAgentLogisticsProduct): ProductDraft {
  return {
    pallet_family: p.pallet_family || '',
    max_extra_pct: p.max_extra_pct != null ? String(p.max_extra_pct) : '',
    max_extra_coverage_days:
      p.max_extra_coverage_days != null ? String(p.max_extra_coverage_days) : '',
  };
}

export function SupplyAgentLogisticsMasterEditor({
  supplierId,
  onChanged,
}: SupplyAgentLogisticsMasterEditorProps) {
  const { t } = useTranslation();
  const { isModuleVisible } = useFeatureVisibility();
  const catalogEnabled = isModuleVisible('logistics_catalog', false);
  const [tab, setTab] = useState<EditorTab>('products');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [okMessage, setOkMessage] = useState<string | null>(null);

  const [products, setProducts] = useState<SupplyAgentLogisticsProduct[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [drafts, setDrafts] = useState<Record<number, ProductDraft>>({});
  const [savingProductId, setSavingProductId] = useState<number | null>(null);

  const [rules, setRules] = useState<SupplyAgentLogisticsRule[]>([]);
  const [ruleForm, setRuleForm] = useState<RuleFormState | null>(null);
  const [savingRule, setSavingRule] = useState(false);

  const [containers, setContainers] = useState<SupplyAgentLogisticsContainer[]>([]);
  const [catalogTemplates, setCatalogTemplates] = useState<LogisticsUnitTemplate[]>([]);
  const [catalogAssignments, setCatalogAssignments] = useState<LogisticsUnitAssignment[]>([]);
  const [savingDefaultTemplateId, setSavingDefaultTemplateId] = useState<number | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [prodRes, ruleRes, contRes, templates, assignments] = await Promise.all([
        supplyAgentService.logisticsListProducts(supplierId),
        supplyAgentService.logisticsListRules(supplierId),
        supplyAgentService.logisticsListContainers(supplierId),
        catalogEnabled
          ? logisticsCatalogService
              .listTemplates()
              .then((rows) =>
                (rows || []).filter((tpl) => tpl.kind === 'CONTAINER' || tpl.kind === 'TRUCK'),
              )
              .catch(() => [] as LogisticsUnitTemplate[])
          : Promise.resolve([] as LogisticsUnitTemplate[]),
        catalogEnabled
          ? logisticsCatalogService
              .listAssignments({ supplier_id: supplierId })
              .catch(() => [] as LogisticsUnitAssignment[])
          : Promise.resolve([] as LogisticsUnitAssignment[]),
      ]);
      setProducts(prodRes.products || []);
      const nextDrafts: Record<number, ProductDraft> = {};
      for (const p of prodRes.products || []) {
        nextDrafts[p.id] = productToDraft(p);
      }
      setDrafts(nextDrafts);
      setRules(ruleRes.rules || []);
      setContainers(contRes.containers || []);
      setCatalogTemplates(templates || []);
      setCatalogAssignments(assignments || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('supply.supplyAgent.logisticsMasterLoadError'));
    } finally {
      setLoading(false);
    }
  }, [supplierId, t, catalogEnabled]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const saveProduct = async (productId: number) => {
    const draft = drafts[productId];
    if (!draft) return;
    setSavingProductId(productId);
    setError(null);
    setOkMessage(null);
    try {
      const updated = await supplyAgentService.logisticsPatchProduct(productId, {
        pallet_family: draft.pallet_family,
        max_extra_pct: parseOptionalFloat(draft.max_extra_pct),
        max_extra_coverage_days: parseOptionalFloat(draft.max_extra_coverage_days),
      });
      setProducts((prev) => prev.map((p) => (p.id === productId ? updated.product : p)));
      setDrafts((prev) => ({ ...prev, [productId]: productToDraft(updated.product) }));
      setOkMessage(t('supply.supplyAgent.logisticsMasterSaveOk'));
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('supply.supplyAgent.logisticsMasterSaveError'));
    } finally {
      setSavingProductId(null);
    }
  };

  const transportTemplates = catalogTemplates.filter(
    (tpl) => tpl.active && (tpl.kind === 'CONTAINER' || tpl.kind === 'TRUCK'),
  );
  const defaultContainerTemplateId =
    catalogAssignments.find((a) => a.active && a.role === 'DEFAULT_CONTAINER')?.template_id ?? null;

  const setDefaultCatalogContainer = async (templateId: number) => {
    setSavingDefaultTemplateId(templateId);
    setError(null);
    setOkMessage(null);
    try {
      await logisticsCatalogService.createAssignment({
        supplier_id: supplierId,
        template_id: templateId,
        role: 'DEFAULT_CONTAINER',
        priority: 100,
      });
      const assignments = await logisticsCatalogService.listAssignments({ supplier_id: supplierId });
      setCatalogAssignments(assignments || []);
      setOkMessage(t('supply.supplyAgent.logisticsMasterDefaultContainerOk'));
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('supply.supplyAgent.logisticsMasterSaveError'));
    } finally {
      setSavingDefaultTemplateId(null);
    }
  };

  const submitRuleForm = async () => {
    if (!ruleForm) return;
    setSavingRule(true);
    setError(null);
    setOkMessage(null);
    const payload = {
      name: ruleForm.name,
      external_rule_id: ruleForm.external_rule_id,
      active: ruleForm.active,
      constraint_type: ruleForm.constraint_type,
      container_code: ruleForm.container_code,
      container_template_id: ruleForm.container_template_id
        ? Number(ruleForm.container_template_id)
        : null,
      target_value: parseOptionalFloat(ruleForm.target_value),
      minimum_value: parseOptionalFloat(ruleForm.minimum_value),
      maximum_value: parseOptionalFloat(ruleForm.maximum_value),
      constraint_unit: ruleForm.constraint_unit,
      adjustment_direction: ruleForm.adjustment_direction,
      optimization_strategy: ruleForm.optimization_strategy,
      max_extra_pct_default: parseOptionalFloat(ruleForm.max_extra_pct_default),
      max_extra_coverage_days_default: parseOptionalFloat(ruleForm.max_extra_coverage_days_default),
      incompatible_product_policy: ruleForm.incompatible_product_policy,
      allow_mixed_products: ruleForm.allow_mixed_products,
      priority: parseOptionalFloat(ruleForm.priority) ?? 100,
      notes: ruleForm.notes,
    };
    try {
      if (ruleForm.id != null) {
        const res = await supplyAgentService.logisticsPatchRule(ruleForm.id, payload);
        setRules((prev) => prev.map((r) => (r.id === ruleForm.id ? res.rule : r)));
      } else {
        const res = await supplyAgentService.logisticsCreateRule({
          ...payload,
          supplier_id: supplierId,
        });
        setRules((prev) => [res.rule, ...prev]);
      }
      setRuleForm(null);
      setOkMessage(t('supply.supplyAgent.logisticsMasterSaveOk'));
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('supply.supplyAgent.logisticsMasterSaveError'));
    } finally {
      setSavingRule(false);
    }
  };

  const tabBtn = (id: EditorTab, label: string) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`rounded-md px-2 py-1 text-xs font-medium ${
        tab === id
          ? 'bg-sky-600 text-white'
          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
      }`}
    >
      {label}
    </button>
  );

  const inputClass =
    'w-full rounded border border-slate-200 bg-white px-1.5 py-1 text-xs text-slate-800';
  const labelClass = 'mb-0.5 block text-[10px] font-medium uppercase tracking-wide text-slate-500';

  const productQuery = productSearch.trim().toLowerCase();
  const filteredProducts = productQuery
    ? products.filter((p) => {
        const code = (p.product_code || '').toLowerCase();
        const name = (p.product_name || '').toLowerCase();
        return code.includes(productQuery) || name.includes(productQuery);
      })
    : products;

  if (loading) {
    return (
      <div className="mt-3 rounded-md border border-slate-100 bg-slate-50 px-2.5 py-2 text-xs text-slate-500">
        {t('supply.supplyAgent.logisticsMasterLoadingEditor')}
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-2 rounded-md border border-slate-200 bg-slate-50/80 p-2.5">
      <div className="flex flex-wrap gap-1.5">
        {tabBtn('products', t('supply.supplyAgent.logisticsMasterTabProducts'))}
        {tabBtn('rules', t('supply.supplyAgent.logisticsMasterTabRules'))}
        {tabBtn('containers', t('supply.supplyAgent.logisticsMasterTabContainers'))}
      </div>

      {error ? <p className="text-xs text-rose-600">{error}</p> : null}
      {okMessage ? <p className="text-xs text-emerald-700">{okMessage}</p> : null}

      {tab === 'products' ? (
        products.length === 0 ? (
          <p className="text-xs text-slate-500">{t('supply.supplyAgent.logisticsMasterNoProducts')}</p>
        ) : (
          <div className="space-y-2">
            <input
              type="search"
              className={inputClass}
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              placeholder={t('supply.supplyAgent.logisticsMasterSearchProducts')}
              aria-label={t('supply.supplyAgent.logisticsMasterSearchProducts')}
            />
            {filteredProducts.length === 0 ? (
              <p className="text-xs text-slate-500">
                {t('supply.supplyAgent.logisticsMasterSearchNoResults')}
              </p>
            ) : (
              <div className="max-h-64 overflow-auto rounded border border-slate-200 bg-white">
                <table className="min-w-full text-left text-xs">
                  <thead className="sticky top-0 bg-slate-100 text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColCode')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColName')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColFamily')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColMaxExtraPct')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColMaxExtraDays')}</th>
                      <th className="px-2 py-1.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((p) => {
                      const d = drafts[p.id] || productToDraft(p);
                      return (
                        <tr key={p.id} className="border-t border-slate-100">
                          <td className="px-2 py-1 font-medium text-slate-700">{p.product_code}</td>
                          <td
                            className="max-w-[8rem] truncate px-2 py-1 text-slate-600"
                            title={p.product_name}
                          >
                            {p.product_name}
                          </td>
                          <td className="px-2 py-1">
                            <input
                              className={inputClass}
                              value={d.pallet_family}
                              onChange={(e) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [p.id]: { ...d, pallet_family: e.target.value },
                                }))
                              }
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              className={inputClass}
                              inputMode="decimal"
                              value={d.max_extra_pct}
                              onChange={(e) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [p.id]: { ...d, max_extra_pct: e.target.value },
                                }))
                              }
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              className={inputClass}
                              inputMode="decimal"
                              value={d.max_extra_coverage_days}
                              onChange={(e) =>
                                setDrafts((prev) => ({
                                  ...prev,
                                  [p.id]: { ...d, max_extra_coverage_days: e.target.value },
                                }))
                              }
                            />
                          </td>
                          <td className="px-2 py-1">
                            <button
                              type="button"
                              disabled={savingProductId === p.id}
                              onClick={() => void saveProduct(p.id)}
                              className="inline-flex items-center gap-1 rounded border border-slate-200 bg-slate-50 px-1.5 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                            >
                              <Save className="h-3 w-3" />
                              {savingProductId === p.id
                                ? t('supply.supplyAgent.logisticsMasterSaving')
                                : t('supply.supplyAgent.logisticsMasterSaveRow')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )
      ) : null}

      {tab === 'rules' ? (
        <div className="space-y-2">
          {!ruleForm ? (
            <>
              <button
                type="button"
                onClick={() => setRuleForm(emptyRuleForm())}
                className="inline-flex items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-medium text-sky-800 hover:bg-sky-100"
              >
                <Plus className="h-3.5 w-3.5" />
                {t('supply.supplyAgent.logisticsMasterNewRule')}
              </button>
              {rules.length === 0 ? (
                <p className="text-xs text-slate-500">{t('supply.supplyAgent.logisticsMasterNoRules')}</p>
              ) : (
                <ul className="space-y-1.5">
                  {rules.map((r) => (
                    <li
                      key={r.id}
                      className="flex items-start justify-between gap-2 rounded border border-slate-200 bg-white px-2 py-1.5 text-xs"
                    >
                      <div>
                        <p className="font-medium text-slate-800">{r.name}</p>
                        <p className="text-slate-500">
                          {logisticsConstraintLabel(t, r.constraint_type)} · {r.adjustment_direction}{' '}
                          · {logisticsStrategyLabel(t, r.optimization_strategy)}
                          {r.active ? '' : ' · off'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setRuleForm(ruleToForm(r))}
                        className="inline-flex shrink-0 items-center gap-1 rounded border border-slate-200 px-1.5 py-1 text-[10px] font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <Pencil className="h-3 w-3" />
                        {t('supply.supplyAgent.logisticsMasterEditRule')}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          ) : (
            <div className="space-y-2 rounded border border-slate-200 bg-white p-2">
              <p className="text-xs font-semibold text-slate-700">
                {ruleForm.id != null
                  ? t('supply.supplyAgent.logisticsMasterEditRule')
                  : t('supply.supplyAgent.logisticsMasterNewRule')}
              </p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>{t('supply.supplyAgent.logisticsMasterRuleName')}</label>
                  <input
                    className={inputClass}
                    value={ruleForm.name}
                    onChange={(e) => setRuleForm({ ...ruleForm, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t('supply.supplyAgent.logisticsMasterRuleExternalId')}
                  </label>
                  <input
                    className={inputClass}
                    value={ruleForm.external_rule_id}
                    onChange={(e) =>
                      setRuleForm({ ...ruleForm, external_rule_id: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t('supply.supplyAgent.logisticsMasterRuleConstraint')}
                  </label>
                  <select
                    className={inputClass}
                    value={ruleForm.constraint_type}
                    onChange={(e) =>
                      setRuleForm({ ...ruleForm, constraint_type: e.target.value })
                    }
                  >
                    {CONSTRAINT_TYPES.map((c) => (
                      <option key={c} value={c}>
                        {logisticsConstraintLabel(t, c)}
                      </option>
                    ))}
                  </select>
                </div>
                {catalogEnabled ? (
                <div>
                  <label className={labelClass}>
                    {t('supply.supplyAgent.logisticsMasterRuleCatalogTemplate')}
                  </label>
                  <select
                    className={inputClass}
                    value={ruleForm.container_template_id}
                    onChange={(e) => {
                      const nextId = e.target.value;
                      const tpl = catalogTemplates.find((row) => String(row.id) === nextId);
                      setRuleForm({
                        ...ruleForm,
                        container_template_id: nextId,
                        container_code: tpl?.code || ruleForm.container_code,
                      });
                    }}
                  >
                    <option value="">
                      {t('supply.supplyAgent.logisticsMasterRuleCatalogTemplateNone')}
                    </option>
                    {catalogTemplates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>
                        {tpl.code}
                        {tpl.name ? ` · ${tpl.name}` : ''}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-slate-500">
                    {t('supply.supplyAgent.logisticsMasterRuleCatalogTemplateHelp')}
                  </p>
                </div>
                ) : null}
                <div>
                  <label className={labelClass}>
                    {t('supply.supplyAgent.logisticsMasterRuleContainer')}
                  </label>
                  <input
                    className={inputClass}
                    value={ruleForm.container_code}
                    onChange={(e) =>
                      setRuleForm({ ...ruleForm, container_code: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('supply.supplyAgent.logisticsMasterRuleTarget')}</label>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    value={ruleForm.target_value}
                    onChange={(e) => setRuleForm({ ...ruleForm, target_value: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('supply.supplyAgent.logisticsMasterRuleMin')}</label>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    value={ruleForm.minimum_value}
                    onChange={(e) => setRuleForm({ ...ruleForm, minimum_value: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('supply.supplyAgent.logisticsMasterRuleMax')}</label>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    value={ruleForm.maximum_value}
                    onChange={(e) => setRuleForm({ ...ruleForm, maximum_value: e.target.value })}
                  />
                </div>
                <div>
                  <label className={labelClass}>{t('supply.supplyAgent.logisticsMasterRuleUnit')}</label>
                  <input
                    className={inputClass}
                    value={ruleForm.constraint_unit}
                    onChange={(e) =>
                      setRuleForm({ ...ruleForm, constraint_unit: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t('supply.supplyAgent.logisticsMasterRuleDirection')}
                  </label>
                  <select
                    className={inputClass}
                    value={ruleForm.adjustment_direction}
                    onChange={(e) =>
                      setRuleForm({ ...ruleForm, adjustment_direction: e.target.value })
                    }
                  >
                    {DIRECTIONS.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>
                    {t('supply.supplyAgent.logisticsMasterRuleStrategy')}
                  </label>
                  <select
                    className={inputClass}
                    value={ruleForm.optimization_strategy}
                    onChange={(e) =>
                      setRuleForm({ ...ruleForm, optimization_strategy: e.target.value })
                    }
                  >
                    {STRATEGIES.map((s) => (
                      <option key={s} value={s}>
                        {logisticsStrategyLabel(t, s)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>
                    {t('supply.supplyAgent.logisticsMasterRuleMaxExtraDefault')}
                  </label>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    value={ruleForm.max_extra_pct_default}
                    onChange={(e) =>
                      setRuleForm({ ...ruleForm, max_extra_pct_default: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t('supply.supplyAgent.logisticsMasterRuleMaxDaysDefault')}
                  </label>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    value={ruleForm.max_extra_coverage_days_default}
                    onChange={(e) =>
                      setRuleForm({
                        ...ruleForm,
                        max_extra_coverage_days_default: e.target.value,
                      })
                    }
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    {t('supply.supplyAgent.logisticsMasterRuleMixPolicy')}
                  </label>
                  <select
                    className={inputClass}
                    value={ruleForm.incompatible_product_policy}
                    onChange={(e) =>
                      setRuleForm({
                        ...ruleForm,
                        incompatible_product_policy: e.target.value,
                      })
                    }
                  >
                    {MIX_POLICIES.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>
                    {t('supply.supplyAgent.logisticsMasterRulePriority')}
                  </label>
                  <input
                    className={inputClass}
                    inputMode="numeric"
                    value={ruleForm.priority}
                    onChange={(e) => setRuleForm({ ...ruleForm, priority: e.target.value })}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelClass}>{t('supply.supplyAgent.logisticsMasterRuleNotes')}</label>
                  <input
                    className={inputClass}
                    value={ruleForm.notes}
                    onChange={(e) => setRuleForm({ ...ruleForm, notes: e.target.value })}
                  />
                </div>
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={ruleForm.allow_mixed_products}
                    onChange={(e) =>
                      setRuleForm({ ...ruleForm, allow_mixed_products: e.target.checked })
                    }
                    className="rounded border-slate-300"
                  />
                  {t('supply.supplyAgent.logisticsMasterRuleAllowMixed')}
                </label>
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
                  <input
                    type="checkbox"
                    checked={ruleForm.active}
                    onChange={(e) => setRuleForm({ ...ruleForm, active: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  {t('supply.supplyAgent.logisticsMasterRuleActive')}
                </label>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={savingRule || !ruleForm.name.trim()}
                  onClick={() => void submitRuleForm()}
                  className="rounded-md bg-sky-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-sky-700 disabled:opacity-50"
                >
                  {savingRule
                    ? t('supply.supplyAgent.logisticsMasterSaving')
                    : ruleForm.id != null
                      ? t('supply.supplyAgent.logisticsMasterSaveRow')
                      : t('supply.supplyAgent.logisticsMasterCreateRule')}
                </button>
                <button
                  type="button"
                  disabled={savingRule}
                  onClick={() => setRuleForm(null)}
                  className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                >
                  {t('supply.supplyAgent.logisticsMasterCancelEdit')}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {tab === 'containers' ? (
        <div className="space-y-4">
          {catalogEnabled ? (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-700">
              {t('supply.supplyAgent.logisticsMasterCatalogContainersTitle')}
            </p>
            <p className="text-xs text-slate-500">
              {t('supply.supplyAgent.logisticsMasterCatalogContainersHelp')}
            </p>
            {transportTemplates.length === 0 ? (
              <p className="text-xs text-slate-500">
                {t('supply.supplyAgent.logisticsMasterNoCatalogContainers')}
              </p>
            ) : (
              <div className="overflow-auto rounded border border-slate-200 bg-white">
                <table className="min-w-full text-left text-xs">
                  <thead className="bg-slate-100 text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColCode')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColName')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColVolume')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColWeight')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColMinFill')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColStatus')}</th>
                      <th className="px-2 py-1.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {transportTemplates.map((tpl) => {
                      const assigned = catalogAssignments.find(
                        (a) => a.active && a.template_id === tpl.id,
                      );
                      const isDefault = defaultContainerTemplateId === tpl.id;
                      const vol =
                        assigned?.template_usable_volume_m3 ?? tpl.usable_volume_m3;
                      const weight =
                        assigned?.template_usable_payload_kg ?? tpl.usable_payload_kg;
                      const minFill =
                        assigned?.template_minimum_fill_pct ?? tpl.minimum_fill_pct;
                      return (
                        <tr
                          key={tpl.id}
                          className={`border-t border-slate-100 ${isDefault ? 'bg-emerald-50/60' : ''}`}
                        >
                          <td className="px-2 py-1 font-medium text-slate-700">{tpl.code}</td>
                          <td className="px-2 py-1 text-slate-600">{tpl.name || '—'}</td>
                          <td className="px-2 py-1">{formatMasterNum(vol, 2)}</td>
                          <td className="px-2 py-1">{formatMasterNum(weight, 2)}</td>
                          <td className="px-2 py-1">{formatMasterNum(minFill, 1)}</td>
                          <td className="px-2 py-1">
                            {isDefault ? (
                              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-800">
                                {t('supply.supplyAgent.logisticsMasterDefaultBadge')}
                              </span>
                            ) : assigned ? (
                              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                {assigned.role}
                              </span>
                            ) : (
                              <span className="text-[10px] text-slate-400">
                                {t('supply.supplyAgent.logisticsMasterNotAssigned')}
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1 text-right">
                            <button
                              type="button"
                              disabled={isDefault || savingDefaultTemplateId === tpl.id}
                              onClick={() => void setDefaultCatalogContainer(tpl.id)}
                              className="rounded border border-sky-200 bg-sky-50 px-2 py-1 text-[10px] font-medium text-sky-800 hover:bg-sky-100 disabled:cursor-default disabled:opacity-50"
                            >
                              {savingDefaultTemplateId === tpl.id
                                ? t('supply.supplyAgent.logisticsMasterSaving')
                                : isDefault
                                  ? t('supply.supplyAgent.logisticsMasterDefaultBadge')
                                  : t('supply.supplyAgent.logisticsMasterUseAsDefault')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          ) : null}

          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-700">
              {t('supply.supplyAgent.logisticsMasterExcelContainersTitle')}
            </p>
            <p className="text-xs text-slate-500">
              {t('supply.supplyAgent.logisticsMasterContainersReadonly')}
            </p>
            {containers.length === 0 ? (
              <p className="text-xs text-slate-500">
                {t('supply.supplyAgent.logisticsMasterNoContainers')}
              </p>
            ) : (
              <div className="overflow-auto rounded border border-slate-200 bg-white">
                <table className="min-w-full text-left text-xs">
                  <thead className="bg-slate-100 text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColCode')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColName')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColVolume')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColWeight')}</th>
                      <th className="px-2 py-1.5">{t('supply.supplyAgent.logisticsMasterColMinFill')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {containers.map((c) => (
                      <tr key={c.id} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-medium text-slate-700">{c.container_code}</td>
                        <td className="px-2 py-1 text-slate-600">{c.description || '—'}</td>
                        <td className="px-2 py-1">{formatMasterNum(c.usable_volume_m3, 2)}</td>
                        <td className="px-2 py-1">{formatMasterNum(c.usable_payload_kg, 2)}</td>
                        <td className="px-2 py-1">{formatMasterNum(c.minimum_fill_pct, 1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
